import React from 'react'

import { QueryClient, dehydrate } from '@tanstack/react-query'
import type { GetStaticProps } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'

import Spinner from '@/components/common/Spinner'
import { getLayout } from '@/components/layouts/recipient/Layout'
import { QueryErrorBoundary } from '@/components/usaspending/DataErrorBoundary'
import {
  RecipientViewSkeleton,
  SuspenseRecipientViewV2,
} from '@/components/usaspending/RecipientView'

import {
  fetchRecipientBasicInfo,
  fetchRecipientDashboard,
  fetchRecipientRecentAwards,
} from '@/context/usaspending'

import { usaspendingKeys } from '@/hooks/usaspending/queryKeys'
import { recipientAnalyticsKeys } from '@/hooks/usaspending/useRecipientAnalytics'

import type { NextPageWithLayout, WithRenderedAt } from '@/types/next'

type RecipientPageProps = WithRenderedAt & {
  id: string
  recipientName: string
  dehydratedState: ReturnType<typeof dehydrate>
}

export async function getStaticPaths() {
  return {
    paths: [], // no paths pre-generated at build time
    fallback: true,
  }
}

export const getStaticProps = (async (context) => {
  const id = context.params?.id
  if (!id || typeof id !== 'string') {
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    }
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: { staleTime: 7 * 24 * 60 * 60 * 1000 },
    },
  })

  // Slim ISR: only prefetch the 3 most critical queries.
  // - dashboard: heaviest query (~6 ClickHouse queries batched), powers most sections
  // - recentAwards: needed for table section, avoids client-side auth issues
  // - basicInfo: needed for page header (Suspense boundary, blocks render)
  //
  // Remaining endpoints (profile, stats, timeSeries, hierarchy, awardPortfolio,
  // outlayTimeSeries, ceilingTimeSeries) load client-side and hit Redis-cached
  // backend responses. prefetchQuery catches errors internally, so failed
  // queries don't block the page — the client retries on mount.
  await Promise.all([
    queryClient.prefetchQuery({
      queryKey: recipientAnalyticsKeys.dashboard(id),
      queryFn: () => fetchRecipientDashboard('', id),
    }),
    queryClient.prefetchQuery({
      queryKey: usaspendingKeys.recipientRecentAwards(id, 5),
      queryFn: () => fetchRecipientRecentAwards('', id, 5),
    }),
    queryClient.prefetchQuery({
      queryKey: usaspendingKeys.recipientBasicInfo(id),
      queryFn: () => fetchRecipientBasicInfo('', id),
    }),
  ])
  const dehydratedState = JSON.parse(JSON.stringify(dehydrate(queryClient)))

  const basicInfo = queryClient.getQueryData<{ name: string }>(
    usaspendingKeys.recipientBasicInfo(id),
  )

  return {
    props: {
      id,
      recipientName: basicInfo?.name ?? '',
      dehydratedState,
      __renderedAt: new Date().toISOString(),
    },
    revalidate: 604800, // 7 days
  }
}) satisfies GetStaticProps<RecipientPageProps>

const spinnerFallback = (
  <div className="flex h-full flex-1 items-center justify-center">
    <Spinner className="h-8 w-8" />
  </div>
)

/**
 * Recipient (Company) Profile Page
 *
 * "Federal Data Observatory" dashboard - data-dense but clean.
 * Features time-series trends, award mix charts, geographic maps,
 * set-aside analysis, and competition breakdowns.
 * Uses Suspense boundaries for progressive loading.
 * Path: /recipients/usaspending/[id]
 *
 * ISR: getStaticProps prefetches 3 critical queries (dashboard, recentAwards,
 * basicInfo) and dehydrates the React Query cache. Remaining endpoints load
 * client-side and hit Redis-cached backend responses for instant response.
 * 7-day staleTime on hooks matches ISR revalidation.
 *
 * Error handling: QueryErrorBoundary wraps the view so that when
 * RecipientHeader's useSuspenseQuery(basicInfo) throws for a bad ID,
 * the error bubbles through its Suspense boundary and is caught here
 * as a full-page not-found — before other sections attempt to render.
 */
const RecipientPage: NextPageWithLayout = ({
  id: propsId,
  recipientName,
  dehydratedState,
}: RecipientPageProps) => {
  const router = useRouter()

  // fallback: true — show skeleton while getStaticProps runs for uncached recipients.
  // router.query is empty during the initial fallback render, so extract id from asPath.
  if (router.isFallback) {
    const fallbackId = router.asPath.split('?')[0].split('/').pop() ?? ''
    return fallbackId ? (
      <RecipientViewSkeleton id={fallbackId} />
    ) : (
      spinnerFallback
    )
  }

  // Use props id from getStaticProps, fall back to router query for client-side nav
  const id =
    propsId || (typeof router.query.id === 'string' ? router.query.id : '')

  if (!id) {
    return spinnerFallback
  }

  return (
    <>
      <Head>
        <title>
          {recipientName
            ? `${recipientName} - Sweetspot`
            : 'Recipient Profile - Sweetspot'}
        </title>
      </Head>
      <QueryErrorBoundary
        title="Failed to load recipient"
        notFoundTitle="Recipient Not Found"
        backHref="/recipients/search"
        backLabel="Search Recipients"
        fullPage
      >
        <SuspenseRecipientViewV2 id={id} dehydratedState={dehydratedState} />
      </QueryErrorBoundary>
    </>
  )
}

RecipientPage.getLayout = getLayout
RecipientPage.requiresAuth = false

export default RecipientPage
